/**
 * The one list-ordering rule, shared by the planner engine and the editor's
 * queue preview so they can never disagree. Default order = biggest list
 * first (leadCount DESC nulls-last, name ASC, wiserId tiebreak). A manual
 * order puts its ids first; resolved lists it doesn't mention append in
 * default order — rule-matched newcomers join at the back, never vanish.
 * For Media Devoted | Created By Ciro Autiero
 */

export interface OrderableList {
  wiserId: number;
  name: string | null;
  leadCount: number | null;
}

export function compareListsByDefaultOrder(
  a: OrderableList,
  b: OrderableList,
): number {
  const ca = a.leadCount ?? -1;
  const cb = b.leadCount ?? -1;
  if (ca !== cb) return cb - ca;
  const na = a.name ?? "";
  const nb = b.name ?? "";
  return na.localeCompare(nb) || a.wiserId - b.wiserId;
}

/** Manual ids first (∩ resolved, deduped), the rest keep the given order. */
export function applyManualOrder(
  defaultOrdered: number[],
  manual: number[],
): number[] {
  const resolved = new Set(defaultOrdered);
  const seen = new Set<number>();
  const head: number[] = [];
  for (const wiserId of manual) {
    if (!resolved.has(wiserId) || seen.has(wiserId)) continue;
    seen.add(wiserId);
    head.push(wiserId);
  }
  return [...head, ...defaultOrdered.filter((id) => !seen.has(id))];
}

/** A stored manual order is only usable if it is a clean array of ints. */
export function parseManualOrder(value: unknown): number[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.every((v) => Number.isInteger(v)) ? (value as number[]) : null;
}
